import { createI18n } from 'vue-i18n';
import type { App } from 'vue';
import { dateUtils, appLocale } from 'shared';

import { sentryModel } from '@/shared/model';

const { RU } = appLocale.config.locales;

// All app-level locale files (core + every slice).
// Vite emits a chunk per file; we filter by locale at runtime so only the
// active locale's chunks are fetched. Each file is pre-compiled by the
// Intlify plugin (see build/vite-plugins/i18n.ts).
const appLocaleModules = import.meta.glob<{ default: Record<string, unknown> }>(
  '../../**/config/locales/{ru,kk,en}.json',
);

// Package-level locale files (from the `shared` workspace package).
// Imported via the package alias because import.meta.glob across the
// workspace root is unreliable in production builds.
type LocaleLoader = () => Promise<{ default: Record<string, unknown> }>;
const pkgLocaleLoaders: Record<string, LocaleLoader[]> = {
  ru: [
    () =>
      import('shared/src/features/use-passengers-text/config/locales/ru.json'),
    () => import('shared/src/features/ticket-details/config/locales/ru.json'),
    () => import('shared/src/entities/results-header/config/locales/ru.json'),
  ],
  kk: [
    () =>
      import('shared/src/features/use-passengers-text/config/locales/kk.json'),
    () => import('shared/src/features/ticket-details/config/locales/kk.json'),
    () => import('shared/src/entities/results-header/config/locales/kk.json'),
  ],
  en: [
    () =>
      import('shared/src/features/use-passengers-text/config/locales/en.json'),
    () => import('shared/src/features/ticket-details/config/locales/en.json'),
    () => import('shared/src/entities/results-header/config/locales/en.json'),
  ],
};

function reportChunkFailure(locale: string, reason: unknown) {
  const error = reason instanceof Error ? reason : new Error(String(reason));
  // Sentry hasn't initialised yet when i18n boots, but logBaseError still
  // routes to window.console.error in dev via sentryLib.debugError, and
  // @sentry/vue queues events before init() runs.
  sentryModel.logBaseError({
    error,
    description: `[i18n] Failed to load "${locale}" locale chunk`,
    level: sentryModel.level.ERROR,
  });
}

async function loadAppMessages(
  locale: string,
): Promise<Record<string, unknown>> {
  const entries = Object.entries(appLocaleModules).filter(([key]) =>
    key.endsWith(`/${locale}.json`),
  );
  if (entries.length === 0) {
    return {};
  }
  const results = await Promise.allSettled(
    entries.map(async ([, loader]) => (await loader()).default),
  );
  const loaded: Record<string, unknown>[] = [];
  for (const result of results) {
    if (result.status === 'fulfilled') {
      loaded.push(result.value);
    } else {
      reportChunkFailure(locale, result.reason);
    }
  }
  return Object.assign({}, ...loaded);
}

async function loadPkgMessages(
  locale: string,
): Promise<Record<string, unknown>> {
  const loaders = pkgLocaleLoaders[locale];
  if (!loaders) {
    return {};
  }
  const results = await Promise.allSettled(
    loaders.map(async loader => (await loader()).default),
  );
  const loaded: Record<string, unknown>[] = [];
  for (const result of results) {
    if (result.status === 'fulfilled') {
      loaded.push(result.value);
    } else {
      reportChunkFailure(locale, result.reason);
    }
  }
  return Object.assign({}, ...loaded);
}

async function loadLocaleMessages(
  locale: string,
): Promise<Record<string, unknown>> {
  const [pkg, app] = await Promise.all([
    loadPkgMessages(locale),
    loadAppMessages(locale),
  ]);
  return { ...pkg, ...app };
}

export const withI18n = async (app: App): Promise<void> => {
  const locale = appLocale.lib.getAppLocale();

  // Eager-load the ru fallback in parallel with the active locale so missing
  // keys never flash raw text on first render. Cost: kk/en users download an
  // additional ru chunk-set at startup; trade-off accepted per Part B Phase 3.
  const [activeMessages, fallbackMessages] = await Promise.all([
    loadLocaleMessages(locale),
    locale !== RU ? loadLocaleMessages(RU) : Promise.resolve(null),
  ]);

  const messages: Record<string, Record<string, unknown>> = {
    [locale]: activeMessages,
  };
  if (fallbackMessages) {
    messages[RU] = fallbackMessages;
  }

  const i18n = createI18n({
    locale,
    legacy: false, // https://vue-i18n.intlify.dev/guide/advanced/composition.html#basic-usage
    fallbackLocale: RU,
    warnHtmlMessage: false,
    pluralRules: {
      ru: appLocale.lib.getRussianPluralFormRules,
    },
    messages,
  });

  dateUtils.setLocale(locale);

  app.use(i18n);
};
